import type {
  PendingAdjustmentsResponse,
  PersonListResponse,
  WalletsSummary,
} from '@esportsbudget/shared';
import { adjustmentsTotal, computeTax, todayISO } from '@esportsbudget/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Minus, Plus, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { MoneyInput, moneyInputToMinor } from '@/components/ui/MoneyInput';
import { SearchableSelect, SegmentedControl, Select } from '@/components/ui/Select';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { errorMessage } from '@/lib/errorMessage';
import { useFormatters } from '@/lib/format';
import { useAuth } from '@/providers/AuthProvider';

type TaxMode = 'ORG_PAYS' | 'PERSON_PAYS' | 'NO_TAX';
type AdjustmentType = 'BONUS' | 'PENALTY';

interface AdjustmentRow {
  key: string;
  type: AdjustmentType;
  amount: string;
  note: string;
}

/**
 * Начисление — главный экран продукта.
 *
 * По умолчанию видно четыре поля: кому, сколько, когда и с какого кошелька.
 * Всё остальное — вид начисления, налог, бонусы и штрафы — скрыто за
 * «Подробнее», чтобы обычное начисление зарплаты делалось в три клика.
 */
export function PayoutFormModal({
  open,
  defaultPersonId,
  onClose,
  onSaved,
}: {
  open: boolean;
  defaultPersonId?: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation(['payouts', 'people', 'common', 'finance', 'errors']);
  const { workspace } = useAuth();
  const format = useFormatters();

  const [personId, setPersonId] = useState(defaultPersonId ?? '');
  const [amount, setAmount] = useState('');
  const [dueDate, setDueDate] = useState(todayISO());
  const [walletId, setWalletId] = useState('');

  const [advanced, setAdvanced] = useState(false);
  const [kind, setKind] = useState('SALARY');
  const [periodKey, setPeriodKey] = useState(todayISO().slice(0, 7));
  const [taxOverride, setTaxOverride] = useState(false);
  const [taxMode, setTaxMode] = useState<TaxMode>('NO_TAX');
  const [taxRate, setTaxRate] = useState('0');
  const [note, setNote] = useState('');
  const [adjustments, setAdjustments] = useState<AdjustmentRow[]>([]);

  const peopleQuery = useQuery({
    queryKey: ['people', workspace?.id, 'for-payout'],
    queryFn: () => api.get<PersonListResponse>('/people', { page: 1, pageSize: 200 }),
    enabled: open && Boolean(workspace),
  });

  const walletsQuery = useQuery({
    queryKey: ['wallets', workspace?.id, false],
    queryFn: () => api.get<WalletsSummary>('/wallets'),
    enabled: open && Boolean(workspace),
  });

  const person = peopleQuery.data?.items.find((item) => item.id === personId);
  const wallets = useMemo(
    () => (walletsQuery.data?.wallets ?? []).filter((wallet) => !wallet.isArchived),
    [walletsQuery.data],
  );

  // Валюта берётся из контракта человека, иначе из настроек организации.
  const currency = person?.currency ?? workspace?.baseCurrency ?? 'RUB';

  useEffect(() => {
    if (!open) return;
    setPersonId(defaultPersonId ?? '');
    setAmount('');
    setDueDate(todayISO());
    setWalletId('');
    setAdvanced(false);
    setKind('SALARY');
    setPeriodKey(todayISO().slice(0, 7));
    setTaxOverride(false);
    setNote('');
    setAdjustments([]);
  }, [open, defaultPersonId]);

  // При выборе человека подставляем его ставку, налог и подходящий кошелёк.
  useEffect(() => {
    if (!person) return;

    if (person.salaryMinor !== null && amount === '') {
      setAmount(String(person.salaryMinor / 100));
    }
    if (person.taxMode) setTaxMode(person.taxMode as TaxMode);
    if (person.taxRate !== null) setTaxRate(String(person.taxRate));
    if (person.nextPayoutDate) setDueDate(person.nextPayoutDate);

    const matching = wallets.find((wallet) => wallet.currency === (person.currency ?? currency));
    if (matching && !walletId) setWalletId(matching.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [person?.id, wallets.length]);

  const baseMinor = moneyInputToMinor(amount, currency) ?? 0;
  const rate = Number(taxRate.replace(',', '.')) || 0;
  const tax = computeTax({ baseMinor, mode: taxMode, ratePercent: rate });

  const adjustmentTotal = adjustmentsTotal(
    adjustments.map((row) => ({
      type: row.type,
      amountMinor: moneyInputToMinor(row.amount, currency) ?? 0,
    })),
  );

  /**
   * Премии и штрафы, заведённые до начисления.
   *
   * Сервер прикрепляет их сам, как только начисление создано, — но человек
   * этого не видел: он вводил оклад, видел в итоге ровно оклад и узнавал про
   * штраф уже после сохранения. Показываем их здесь и считаем в итоге, чтобы
   * сумма на экране совпадала с той, что получится.
   *
   * Отправлять их в `adjustments` нельзя: сервер прикрепит существующие
   * записи, а присланные создаст заново — и штраф удвоится.
   */
  const effectivePeriod = kind === 'BONUS' || kind === 'PRIZE' ? dueDate.slice(0, 7) : periodKey;

  const pendingQuery = useQuery({
    queryKey: ['payouts', 'pending', workspace?.id, effectivePeriod],
    queryFn: () =>
      api.get<PendingAdjustmentsResponse>('/payouts/adjustments/pending', {
        periodKey: effectivePeriod,
      }),
    enabled: open && Boolean(workspace) && Boolean(effectivePeriod),
  });

  // Прикрепляются только совпадающие по человеку, периоду и валюте —
  // теми же условиями, что и на сервере.
  const pendingRows = useMemo(
    () =>
      (pendingQuery.data?.items ?? []).filter(
        (row) => row.personId === personId && row.currency === currency,
      ),
    [pendingQuery.data, personId, currency],
  );

  const pendingTotal = adjustmentsTotal(pendingRows);

  const totalNet = tax.netMinor + adjustmentTotal + pendingTotal;

  const mutation = useMutation({
    mutationFn: (payImmediately: boolean) =>
      api.post('/payouts', {
        personId,
        baseMinor,
        dueDate,
        walletId: walletId || null,
        kind,
        // Период отправляем всегда, а не только из «Подробнее»: к нему
        // привязываются премии и штрафы за месяц, и без него они не войдут
        // в начисление — а человек об этом даже не узнает.
        periodKey: kind === 'BONUS' || kind === 'PRIZE' ? null : periodKey,
        ...(taxOverride ? { taxMode, taxRate: rate } : {}),
        note: note.trim() || null,
        adjustments: adjustments
          .filter((row) => (moneyInputToMinor(row.amount, currency) ?? 0) > 0)
          .map((row) => ({
            type: row.type,
            amountMinor: moneyInputToMinor(row.amount, currency)!,
            note: row.note.trim() || null,
          })),
        progressStatus: 'NOT_STARTED',
        payImmediately,
      }),
    onSuccess: (_data, payImmediately) => {
      toast.success(payImmediately ? t('payouts:createdAndPaid') : t('payouts:created'));
      onSaved();
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  function addAdjustment(type: AdjustmentType) {
    setAdjustments((rows) => [
      ...rows,
      { key: `${Date.now()}-${rows.length}`, type, amount: '', note: '' },
    ]);
    setAdvanced(true);
  }

  const canSubmit = Boolean(personId) && baseMinor > 0 && !mutation.isPending;
  const canPayNow = canSubmit && Boolean(walletId);

  return (
    <Modal
      open={open}
      onClose={onClose}
      busy={mutation.isPending}
      title={t('payouts:createTitle')}
      size="md"
      footer={
        <>
          {/* Деньги уходят с кошелька — это необратимо, поэтому выплата стоит
              второстепенной кнопкой и требует подтверждения. Раньше она была
              главной, а кошелёк подставлялся сам: начисление превращалось в
              выплату одним нажатием, и человек узнавал об этом постфактум. */}
          <Button
            variant="secondary"
            disabled={!canPayNow}
            loading={mutation.isPending && mutation.variables === true}
            title={!walletId ? t('payouts:pickWalletFirst') : undefined}
            onClick={() => {
              if (window.confirm(t('payouts:confirmAccrueAndPay', {
                amount: format.money(tax.netMinor, currency),
                wallet: wallets.find((item) => item.id === walletId)?.name ?? '',
              }))) {
                mutation.mutate(true);
              }
            }}
          >
            {t('payouts:accrueAndPay')}
          </Button>
          <Button
            disabled={!canSubmit}
            loading={mutation.isPending && !mutation.variables}
            onClick={() => mutation.mutate(false)}
          >
            {t('payouts:accrue')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <SearchableSelect
          label={t('payouts:toWhom')}
          placeholder={t('payouts:pickPerson')}
          options={(peopleQuery.data?.items ?? []).map((item) => ({
            value: item.id,
            label: item.nickname,
            hint: item.roleTitle ?? undefined,
          }))}
          value={personId}
          onChange={setPersonId}
        />

        <MoneyInput
          label={t('payouts:amount')}
          value={amount}
          onChange={setAmount}
          currency={currency}
          large
          hint={
            person?.salaryMinor
              ? t('payouts:fromContract', {
                  amount: format.money(person.salaryMinor, person.currency ?? currency),
                })
              : t('finance:amountCalcHint')
          }
        />

        <div className="grid gap-3 sm:grid-cols-2">
          <Input
            type="date"
            label={t('payouts:dueDate')}
            value={dueDate}
            onChange={(event) => setDueDate(event.target.value)}
          />
          <SearchableSelect
            label={t('finance:wallet')}
            placeholder={t('payouts:noWallet')}
            options={wallets.map((wallet) => ({
              value: wallet.id,
              label: wallet.name,
              hint: format.money(wallet.balanceMinor, wallet.currency),
              // Кошелёк в другой валюте выбрать нельзя: пересчёт по выдуманному
              // курсу дал бы в отчёте цифры, которых не было.
              disabled: wallet.currency !== currency,
            }))}
            value={walletId}
            hint={t('payouts:walletHint')}
            onChange={setWalletId}
          />
        </div>

        {/* Бонусы и штрафы (F-34) */}
        {adjustments.length > 0 && (
          <div className="flex flex-col gap-2">
            {adjustments.map((row, index) => (
              <div key={row.key} className="flex items-start gap-2">
                <span
                  className={cn(
                    'mt-2 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-[13px] font-semibold',
                    row.type === 'BONUS'
                      ? 'bg-success-soft text-success'
                      : 'bg-danger-soft text-danger',
                  )}
                  title={t(row.type === 'BONUS' ? 'payouts:bonus' : 'payouts:penalty')}
                >
                  {row.type === 'BONUS' ? '+' : '−'}
                </span>

                <div className="w-[130px] shrink-0">
                  <MoneyInput
                    value={row.amount}
                    currency={currency}
                    onChange={(value) =>
                      setAdjustments((rows) =>
                        rows.map((item, i) => (i === index ? { ...item, amount: value } : item)),
                      )
                    }
                  />
                </div>

                <Input
                  placeholder={t(
                    row.type === 'BONUS' ? 'payouts:bonusReason' : 'payouts:penaltyReason',
                  )}
                  value={row.note}
                  wrapperClassName="flex-1"
                  onChange={(event) =>
                    setAdjustments((rows) =>
                      rows.map((item, i) =>
                        i === index ? { ...item, note: event.target.value } : item,
                      ),
                    )
                  }
                />

                <Button
                  variant="ghost"
                  size="icon"
                  className="mt-0.5"
                  aria-label={t('common:delete')}
                  onClick={() => setAdjustments((rows) => rows.filter((_, i) => i !== index))}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <Button variant="ghost" size="sm" icon={<Plus />} onClick={() => addAdjustment('BONUS')}>
            {t('payouts:addBonus')}
          </Button>
          <Button variant="ghost" size="sm" icon={<Minus />} onClick={() => addAdjustment('PENALTY')}>
            {t('payouts:addPenalty')}
          </Button>
        </div>

        {/* Живой расчёт — всегда виден, чтобы не считать налог в калькуляторе */}
        {baseMinor > 0 && (
          <div className="rounded-[10px] border border-border bg-surface-2 px-4 py-3">
            <div className="flex flex-col gap-1.5 text-[13.5px]">
              <CalcRow label={t('payouts:calcAccrued')} value={format.money(tax.grossMinor, currency)} />

              {taxMode !== 'NO_TAX' && (
                <CalcRow
                  label={t(taxMode === 'ORG_PAYS' ? 'people:calcTaxOrg' : 'people:calcTaxPerson', {
                    rate,
                  })}
                  value={format.money(tax.taxMinor, currency)}
                />
              )}

              {/* Заведённые заранее премии и штрафы: сервер прикрепит их сам,
                  здесь они только показываются и участвуют в итоге. */}
              {pendingRows.map((row) => (
                <CalcRow
                  key={row.id}
                  label={`${row.type === 'BONUS' ? '+' : '−'} ${
                    row.note?.trim() ||
                    t(row.type === 'BONUS' ? 'payouts:bonus' : 'payouts:penalty')
                  } · ${t('payouts:pendingWillAttach')}`}
                  value={format.money(row.amountMinor, currency)}
                  tone={row.type === 'BONUS' ? 'success' : 'danger'}
                />
              ))}

              {adjustments
                .filter((row) => (moneyInputToMinor(row.amount, currency) ?? 0) > 0)
                .map((row, index) => (
                  <CalcRow
                    key={row.key}
                    label={`${row.type === 'BONUS' ? '+' : '−'} ${
                      row.note.trim() ||
                      t(row.type === 'BONUS' ? 'payouts:bonus' : 'payouts:penalty')
                    }`}
                    value={format.money(moneyInputToMinor(row.amount, currency)!, currency)}
                    tone={row.type === 'BONUS' ? 'success' : 'danger'}
                  />
                ))}

              <div className="mt-1.5 border-t border-border pt-2">
                <CalcRow
                  label={t('payouts:calcNet')}
                  value={format.money(totalNet, currency)}
                  bold
                />
              </div>
            </div>
          </div>
        )}

        {advanced ? (
          <div className="flex flex-col gap-4 border-t border-border pt-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <Select
                label={t('payouts:kind')}
                options={['SALARY', 'ADVANCE', 'BONUS', 'PRIZE', 'REIMBURSEMENT', 'PENALTY', 'OTHER'].map(
                  (value) => ({ value, label: t(`payouts:kindValue.${value}`) }),
                )}
                value={kind}
                onChange={(event) => setKind(event.target.value)}
              />
              <Input
                type="month"
                label={t('payouts:period')}
                hint={t('payouts:periodHint')}
                value={periodKey}
                onChange={(event) => setPeriodKey(event.target.value)}
              />
            </div>

            <div className="rounded-[10px] border border-border p-3.5">
              <label className="flex cursor-pointer items-center gap-2.5">
                <input
                  type="checkbox"
                  checked={taxOverride}
                  onChange={(event) => setTaxOverride(event.target.checked)}
                  className="h-4 w-4 accent-[var(--eb-accent)]"
                />
                <span className="text-[13.5px] font-medium">{t('payouts:overrideTax')}</span>
              </label>

              {taxOverride && (
                <div className="mt-3.5 flex flex-col gap-3">
                  <SegmentedControl<TaxMode>
                    value={taxMode}
                    onChange={setTaxMode}
                    options={[
                      { value: 'ORG_PAYS', label: t('people:taxOrgPays') },
                      { value: 'PERSON_PAYS', label: t('people:taxPersonPays') },
                      { value: 'NO_TAX', label: t('people:taxNone') },
                    ]}
                  />
                  {taxMode !== 'NO_TAX' && (
                    <div className="flex items-center gap-2.5">
                      <Input
                        label={t('people:taxRate')}
                        inputMode="decimal"
                        className="tabular text-right"
                        value={taxRate}
                        wrapperClassName="w-[120px]"
                        onChange={(event) => setTaxRate(event.target.value)}
                      />
                      <span className="mt-6 text-[13px] text-muted">%</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            <Textarea
              label={t('finance:comment')}
              placeholder={t('payouts:notePlaceholder')}
              value={note}
              maxLength={500}
              onChange={(event) => setNote(event.target.value)}
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setAdvanced(true)}
            className="self-start text-[13px] font-medium text-accent hover:underline"
          >
            {t('payouts:showAdvanced')} ▾
          </button>
        )}
      </div>
    </Modal>
  );
}

function CalcRow({
  label,
  value,
  bold,
  tone,
}: {
  label: string;
  value: string;
  bold?: boolean;
  tone?: 'success' | 'danger';
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className={cn('text-muted', tone === 'danger' && 'text-danger', tone === 'success' && 'text-success')}>
        {label}
      </span>
      <span
        className={cn(
          'tabular font-medium',
          bold && 'text-[15px] font-semibold',
          tone === 'danger' && 'text-danger',
          tone === 'success' && 'text-success',
        )}
      >
        {value}
      </span>
    </div>
  );
}
